import React, {
  useMemo,
  useCallback,
  useState,
  useEffect
} from 'react';
import ButtonMC from '../../components/ButtonMC';
import IconMC from '../../components/IconMC';

/**
 * FilterSectionMC - Molécula específica para secciones de filtros del Inbox
 * Optimizada con useMemo y useCallback para evitar renders innecesarios.
 * Atomic Design: Molecule (Inbox domain)
 *
 * Soporta un acordeón opcional por ítem: si `filterItem.accounts` tiene 2 o más
 * cuentas (ej. páginas de Messenger), la fila mantiene su acción de filtrar todo
 * el canal y se añade una flecha dedicada a la derecha que despliega las páginas.
 *
 * @param {string} title - Título de la sección
 * @param {Array} filters - Array de filtros: { key, label, icon, count, accounts? }
 * @param {string} activeFilter - Filtro actualmente activo
 * @param {function} onFilterChange - Callback al cambiar filtro
 * @param {string} activeSubFilter - Id de la página/cuenta actualmente seleccionada
 * @param {function} onSubFilterChange - Callback (channelKey, accountId) al elegir una página
 * @param {string} iconType - 'icon' (IconMC) o 'image' (img tag)
 * @param {string} buttonClassName - Clases extra para el botón
 * @param {string} countClassName - Clases extra para el contador
 * @param {string} labelClassName - Clases extra para el label
 * @param {boolean} showCloseIcon - Mostrar ícono de cerrar (default: true)
 * @param {function} onCloseClick - Callback al cerrar filtro
 */
const FilterSectionMC = React.memo(({
  title,
  filters,
  activeFilter,
  onFilterChange,
  activeSubFilter = null,
  onSubFilterChange,
  iconType = 'icon',
  buttonClassName = '',
  countClassName = '',
  labelClassName = '',
  showCloseIcon = true,
  onCloseClick
}) => {
  const [expandedKeys, setExpandedKeys] = useState({});

  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    if (activeFilter && activeSubFilter) {
      setExpandedKeys((prev) => (prev[activeFilter]
        ? prev
        : { ...prev, [activeFilter]: true }));
    }
  }, [activeFilter, activeSubFilter]);

  // Memoiza los filtros renderizados
  const renderedFilters = useMemo(() => filters.map((filterItem) => {
    const isActive = activeFilter === filterItem.key;
    const hasAccounts = Array.isArray(filterItem.accounts)
        && filterItem.accounts.length >= 2;
    const isExpanded = !!expandedKeys[filterItem.key];

    const buttonClasses = [
      'tw-w-full !tw-justify-start tw-h-10 tw-rounded-md tw-transition-colors',
      isActive
        ? '!tw-text-blue-500 !tw-bg-blue-50'
        : 'tw-text-gray-700 hover:!tw-bg-gray-50',
      buttonClassName
    ].join(' ');

    const iconClasses = isActive
      ? '!tw-text-blue-500 !tw-bg-blue-50'
      : 'tw-text-gray-700 hover:!tw-bg-gray-50';

    const countClasses = [
      'tw-absolute tw-transform -tw-translate-y-1/2 tw-text-2xs tw-py-1',
      // Deja espacio para la flecha cuando el canal es multipágina.
      hasAccounts ? 'tw-right-9 tw-top-5' : 'tw-right-3 tw-top-1/2',
      isActive
        ? '!tw-text-blue-500 !tw-bg-blue-50'
        : 'tw-text-gray-400 hover:!tw-bg-gray-50',
      countClassName
    ].join(' ');

    const labelClasses = ['!tw-text-xs !tw-font-normal', labelClassName].join(' ');

    // Handler de cerrar filtro como función normal
    const handleClose = (e) => {
      e.stopPropagation();
      onFilterChange(null);
      if (onCloseClick) onCloseClick();
    };

    const handleToggle = (e) => {
      e.stopPropagation();
      toggleExpanded(filterItem.key);
    };

    return (
      <div key={filterItem.key} className="tw-relative">
        {showCloseIcon && isActive && (
        <div className="tw-absolute -tw-top-2 -tw-right-2 tw-z-10">
          <button
            type="button"
            onClick={handleClose}
            className="tw-bg-red-500 tw-rounded-sm tw-p-1 tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-transition-opacity hover:tw-opacity-80"
            title="Cerrar"
          >
            <IconMC
              name="close-outline"
              size="2xs"
              className="tw-text-white"
            />
          </button>
        </div>
        )}
        <ButtonMC
          variant="TEXT_PLAIN"
          text={filterItem.label}
          onClick={() => onFilterChange(filterItem.key)}
          size="small"
          className={buttonClasses}
          renderContent={() => (
            <div className="tw-flex tw-items-center tw-gap-1">
              {iconType === 'icon' ? (
                <IconMC
                  size="base"
                  name={filterItem.icon}
                  className={iconClasses}
                />
              ) : (
                <img
                  src={filterItem.icon}
                  alt={filterItem.label}
                  className="tw-w-4 tw-h-4"
                />
              )}
              <span className={labelClasses}>
                {filterItem.label}
              </span>
            </div>
          )}
        />
        <span className={countClasses}>
          {filterItem.count}
        </span>
        {hasAccounts && (
          <button
            type="button"
            onClick={handleToggle}
            aria-label={filterItem.label}
            aria-expanded={isExpanded}
            className="tw-absolute tw-right-2 tw-top-5 tw-transform -tw-translate-y-1/2 tw-p-1 tw-text-gray-500 hover:tw-text-gray-700"
          >
            <IconMC
              name={`arrow-ios-${isExpanded ? 'up' : 'down'}ward-outline`}
              size="base"
            />
          </button>
        )}
        {hasAccounts && isExpanded && (
        <div className="tw-flex tw-flex-col tw-pl-6">
          {filterItem.accounts.map((account) => {
            const isAccountActive = isActive
                  && String(activeSubFilter) === String(account.id);
            const accountClasses = [
              'tw-relative tw-w-full tw-flex tw-items-center tw-justify-between tw-h-9 tw-px-2 tw-rounded-md tw-transition-colors',
              isAccountActive
                ? '!tw-text-blue-500 !tw-bg-blue-50'
                : 'tw-text-gray-700 hover:!tw-bg-gray-50'
            ].join(' ');

            return (
              <button
                key={account.id ?? account.name}
                type="button"
                onClick={() => onSubFilterChange
                      && onSubFilterChange(filterItem.key, account.id, account.platform)}
                className={accountClasses}
              >
                <span className="tw-truncate tw-text-left !tw-text-xs !tw-font-normal">
                  {account.name}
                </span>
              </button>
            );
          })}
        </div>
        )}
      </div>
    );
  }), [
    filters,
    activeFilter,
    onFilterChange,
    activeSubFilter,
    onSubFilterChange,
    expandedKeys,
    toggleExpanded,
    iconType,
    buttonClassName,
    countClassName,
    labelClassName,
    showCloseIcon,
    onCloseClick
  ]);

  return (
    <div className="tw-wrapper tw-px-2 tw-mb-2">
      <div className="tw-w-full">
        <div className="tw-flex tw-items-center tw-justify-between tw-py-2 tw-cursor-pointer tw-gap-2 tw-text-gray-500">
          <div className="tw-flex tw-items-center tw-gap-1">
            <span className="tw-truncate tw-text-xs tw-font-medium tw-tracking-wide">
              {title}
            </span>
          </div>
        </div>
        {renderedFilters}
      </div>
    </div>
  );
});

export default FilterSectionMC;
